import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { FC, useMemo } from 'react';

import { NS } from '@/constants';
import { FULL_SCREEN_CONTAINER_MOBILE } from '@/constants/gui';
import { URLS } from '@/constants/urls';
import { FilterModel } from '@/models';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { useAppDispatch, useAppSelector } from '@/redux';
import { showFullScreen } from '@/redux/slices/gui.slice';
import { CallToActionKind } from '@/types';

import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';

import styles from './CategFilters.module.scss';
import { FILTERS } from '@/constants/categ';
import Switch from '@/components/common/design-system/Switch.ds';

const FilterMobile = dynamic(() => import('@/components/categ/filters/FilterMobile'), { ssr: false });
FilterMobile.displayName = 'FilterMobile';
const SortMobile = dynamic(() => import('@/components/categ/filters/SortMobile'), { ssr: false });
SortMobile.displayName = 'SortMobile';

type Props = {
  filters?: FilterModel[];
  deleteFilter?: (filter: unknown) => void;
  deleteAll?: () => void;
  onSortChange?: (sort: number) => void;
  onFilterChange?: (filter: unknown) => void;
  sort?: number;
  selectedFiltersInfos?: FilterModel[];
  withPadding?: boolean;
  isProducerCategory?: boolean;
  showCUProducts?: () => void;
  onlyCUChecked?: boolean;
  isFromWB?: boolean;
  isFromCU?: boolean;
  innerModal?: boolean;
  isSwitchDisable?: boolean;
}

const CategFilters: FC<Props> = ({
  filters,
  deleteFilter,
  deleteAll,
  onSortChange,
  onFilterChange,
  sort,
  selectedFiltersInfos,
  withPadding,
  isProducerCategory = false,
  showCUProducts,
  onlyCUChecked,
  innerModal = false,
  isFromWB = false,
  isFromCU = false,
  isSwitchDisable = false
}) => {
  const { t } = useTranslation([NS.COMMON, NS.CATEG]);
  const dispatch = useAppDispatch();
  const { isFilterMobileVisible, isSortMobileVisible } = useAppSelector(({ gui }) => gui);
  const isMobile = useScreenSizeContext().isMobile;
  const isDesktop = useScreenSizeContext().isDesktop;

  const mobileFilters = useMemo(() => filters?.filter((filter) =>
    filter.displayable && (filter.nbByValue.length > 1 || selectedFiltersInfos.some((s) => s.nameId === filter.nameId))
  ), [filters]);

  const topFiveFilters = useMemo(() => mobileFilters?.slice(0, 5), [mobileFilters, selectedFiltersInfos]);

  const showFilters = () => (
    mobileFilters?.length > 0 &&
    dispatch(showFullScreen({ fullScreenType: FULL_SCREEN_CONTAINER_MOBILE.FILTER, onShow: undefined, isMobile }))
  );

  const customClassName = [
    styles.container,
    withPadding && styles['with-padding']
  ].filter(Boolean).join(' ');

  return (
    <>
      <div className={customClassName}>
        <div className={styles.buttons} data-rbf="categ-filters-mobile">
          <CallToActionContainer
            kind={CallToActionKind.SECONDARY}
            fullWidth
            onClick={() => showFilters()}
            icon={{
              url: URLS.ICON_FILTERS,
              alt: t('alter:filterProducts'),
              width: 16,
              height: 16
            }}
          >
            {t('btn.filter')}
          </CallToActionContainer>
          {topFiveFilters?.length > 0 && topFiveFilters.map((filter, i) => {
            return (
              <CallToActionContainer
                key={i}
                data-test-id="top-filter"
                kind={CallToActionKind.SECONDARY}
                fullWidth
                onClick={() => dispatch(showFullScreen({
                  fullScreenType: FULL_SCREEN_CONTAINER_MOBILE.FILTER,
                  selectedFilter: filter, isMobile
                }))}
              >
                {filter.name}
              </CallToActionContainer>
            );
          })}
          {isDesktop && !isFromCU && (
            <Switch
              defaultState={onlyCUChecked}
              onClick={showCUProducts}
              disabled={isSwitchDisable}
              paragraph={t(`filter.${FILTERS.ONLY_CU}`, { ns: NS.CATEG })}
              customClassName={styles.switch}
            />
          )}
          {!isDesktop && (
            <CallToActionContainer
              kind={CallToActionKind.SECONDARY}
              fullWidth
              onClick={() => dispatch(
                showFullScreen({ fullScreenType: FULL_SCREEN_CONTAINER_MOBILE.SORT, onShow: undefined, isMobile })
              )}
            >
              {t('category:sort')}
            </CallToActionContainer>
          )}
        </div>
      </div>
      {isFilterMobileVisible && innerModal && (
        <FilterMobile
          onChange={onFilterChange}
          onDelete={deleteFilter}
          deleteAll={deleteAll}
          selectedFiltersInfos={selectedFiltersInfos}
          filters={mobileFilters}
          showCUProducts={showCUProducts}
          onlyCUChecked={onlyCUChecked}
          isFromWB={isFromWB}
          sort={sort}
        />
      )}
      {isSortMobileVisible && innerModal && (
        <SortMobile selectedSort={sort} onChange={onSortChange} isProducerCategory={isProducerCategory} />
      )}
    </>
  );
};

export default CategFilters;
